import React from 'react';
import {
  AVAILABLE_COINS as AVAILABLE_COINS_FALLBACK,
  COIN_COLORS as COIN_COLORS_FALLBACK,
  MAX_COINS,
} from '../types';
import { useCoinRegistry } from '../../../shared/CoinRegistryContext';

interface CoinSelectorProps {
  selected: string[];
  onChange: (selected: string[]) => void;
  error?: string;
}

function CoinSelector({ selected, onChange, error }: CoinSelectorProps) {
  const {
    availableCoins: registryCoins,
    coinColors: registryColors,
    disabledCoins,
  } = useCoinRegistry();
  const AVAILABLE_COINS =
    registryCoins.length > 0 ? registryCoins : AVAILABLE_COINS_FALLBACK;
  const COIN_COLORS =
    registryCoins.length > 0 ? registryColors : COIN_COLORS_FALLBACK;

  const toggle = (coin: string) => {
    if (selected.includes(coin)) {
      onChange(selected.filter((c) => c !== coin));
    } else {
      if (selected.length >= MAX_COINS) return;
      onChange([...selected, coin]);
    }
  };

  return (
    <div className="pq-field">
      <label className="pq-label">
        Coins <span className="pq-required">*</span>
      </label>

      <div className="pq-coin-actions">
        <button
          type="button"
          className="pq-coin-action-btn"
          onClick={() => onChange([])}
        >
          Clear
        </button>
      </div>

      <div className="pq-coin-grid">
        {AVAILABLE_COINS.map((coin) => {
          const isDisabled = disabledCoins.has(coin);
          const isActive = selected.includes(coin);
          const color = COIN_COLORS[coin] || '#8b949e';
          const atLimit = !isActive && selected.length >= MAX_COINS;
          const blocked = isDisabled || atLimit;

          return (
            <button
              key={coin}
              type="button"
              className={`pq-coin-pill ${isActive ? 'active' : ''} ${blocked ? 'disabled' : ''} ${isDisabled ? 'pq-coin-pill--off' : ''}`}
              onClick={() => !blocked && toggle(coin)}
              title={
                isDisabled
                  ? `${coin} is disabled in Coin Management`
                  : undefined
              }
              style={
                isActive && !isDisabled
                  ? {
                      borderColor: color,
                      background: hexToRgba(color, 0.15),
                      color: color === '#000000' ? '#e1e4e8' : color,
                      boxShadow: `0 0 12px ${hexToRgba(color, 0.25)}, inset 0 0 8px ${hexToRgba(color, 0.1)}`,
                    }
                  : undefined
              }
            >
              {coin}
            </button>
          );
        })}
      </div>

      <span
        className={`pq-counter ${selected.length >= MAX_COINS ? 'at-limit' : ''}`}
      >
        {selected.length} / {MAX_COINS} selected
      </span>
      {error && <span className="pq-error pq-error--animate">{error}</span>}
    </div>
  );
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default CoinSelector;
